import React from 'react';
import { makeAutoObservable, runInAction } from 'mobx';
import { DatabusNotification, DeepActionResult, Token } from 'models';
import CNH from 'models/CNHModel';
import * as JDModel from 'models/JohnDeereModel';
import * as TrimbleModel from 'models/TrimbleModel';
import { getProagricaOrganizations, postProagricaUser } from 'models/ProagricaModel';
import Integration, {
  ConnectIntegrationResponse,
  IntegrationConnectionType,
  IntegrationKeys,
  Integrations,
  LicenseStatusResponse,
  PartnerId,
} from 'models/Integration';
import { IntegrationErrorTypes } from './types';
import * as Valet from 'models/Valet';
import { getStorage, setStorage } from 'models/Browser';
import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import get from 'lodash/get';
import { integrationsExtraInformation } from '../../resources/IntegrationsData';
import { getPartnerIdFromIntegrationKey } from './utils/getPartnerIdFromIntegrationKey';
import { redirectToOAuthPortal } from './utils/redirectToOAuthPortal';
import { redirectURI } from './utils/redirectURI';
import { AuthAccount } from 'views/Integrations/types';
import { useIntegrationsStore } from './store/useIntegrationsStore';
import { APIState } from '../../models/APIState';

const CNH_CLIENT_ID = import.meta.env.VITE_APP_CNH_CLIENT_ID ?? 'FAKE_CLIENT_ID';
const CNH_CLIENT_SECRET = import.meta.env.VITE_APP_CNH_CLIENT_SECRET ?? 'FAKE_SECRET';

const ENABLED_INTEGRATION_ENTITLEMENT_KEY = 'integration.enable';

export default class IntegrationsModel {
  _rootStore: any;
  _userId: string = '';
  _activeUser: string = '';
  _baseOrganizations: any[] = [];
  _selectedOrganizations: string[] = [];
  _startDate: string = '';
  _endDate: string = '';
  _token: string = '';
  _active_integration_token: Token | null = null;
  _upTo: boolean = false;
  _between: boolean = false;
  _active_integration: Integration | null | undefined;
  _settings: any = {};
  _status: APIState = APIState.LOADING;
  _setupIntegrationStatus: APIState = APIState.SUCCESS;
  _loadOrganizationsStatus: APIState = APIState.SUCCESS;
  _integrations: Integrations = new Integrations();
  _auto_fft: { is_active?: boolean } = { is_active: undefined };
  sync_fft: string = '';
  CNH: CNH;
  gettingIntegrations: boolean = false;
  _crumbs: any[] = [{ name: 'Integrations', path: '/integrations' }];
  _selectedIntegrationTab: string = 'Machinery';
  _searchString: string = '';
  _organizationCountryCode: string = '';
  _entitlementKeys: string[] = [];
  _licensesApps: LicenseStatusResponse['apps'] = [];

  constructor(rootStore: any) {
    makeAutoObservable(this);
    this._rootStore = rootStore;
    this.CNH = new CNH(CNH_CLIENT_ID, CNH_CLIENT_SECRET);
    this.setActiveIntegration = this.setActiveIntegration.bind(this);
    this.set_subheader = this.set_subheader.bind(this);
    const storage = {
      auto_fft: getStorage('auto_fft'),
      sync_fft: getStorage('sync_fft'),
    };
    if (storage.auto_fft) {
      this._auto_fft = storage.auto_fft;
    }
    if (storage.sync_fft) {
      this.sync_fft = storage.sync_fft;
    }
  }

  set_subheader(integration_name: string, partner_id?: PartnerId) {
    if (this.crumbs.find((crumb) => crumb.name === integration_name)) return;

    runInAction(() => {
      this.crumbs = [
        ...this.crumbs,
        { name: integration_name, path: `integrations/${partner_id}` },
      ];
    });
  }

  set_subheader_default() {
    runInAction(() => {
      this.crumbs = [{ name: 'Integrations', path: '/integrations' }];
    });
  }

  async updateIntegrationsStatus() {
    const integrationStatusData = await Valet.getIntegrationsStatus(this.user_id, this.org_id);
    runInAction(() => {
      this._integrations.integrations.forEach((integration) => {
        const integrationStatus = integrationStatusData.integrations.find(
          ({ partner }) => partner === integration.partner_id,
        );
        if (integrationStatus?.status) integration['status'] = integrationStatus?.status;
      });
    });
  }

  async loadOrganizationLicenses() {
    if (
      !isEmpty(this._licensesApps) ||
      !this._rootStore.orgId ||
      !this._rootStore.auth.token.access_token
    ) {
      return;
    }

    const licenses = await Valet.getOrganizationLicenses(
      this._rootStore.orgId,
      this._rootStore.auth.token.access_token,
    );
    runInAction(() => {
      this._licensesApps = licenses.apps;
    });
  }

  async getOrganizationEntitlements() {
    if (!isEmpty(this._entitlementKeys)) return this._entitlementKeys;

    await this.loadOrganizationLicenses();

    this._entitlementKeys = this._licensesApps
      ?.map((app) => app?.plan.entitlements.map(({ key }) => key))
      ?.flatMap((array) => array);

    return this._entitlementKeys;
  }

  async getIsContract() {
    await this.loadOrganizationLicenses();

    const contractIds = this._licensesApps?.map((app) => app?.id);
    const requiredApps = this.activeIntegration?.requiredApplications;

    return contractIds?.map((contractId) => requiredApps?.includes(contractId)).some(Boolean);
  }

  async loadIntegrations() {
    runInAction(() => {
      this._status = APIState.LOADING;
    });

    const [integrationsData, entitlements] = await Promise.all([
      await Valet.getIntegrations(this._organizationCountryCode),
      await this.getOrganizationEntitlements(),
    ]);

    runInAction(() => {
      this._status = APIState.SUCCESS;
    });

    const integrationOptions = integrationsData
      .filter(
        ({ entitlement_key }) =>
          entitlement_key === ENABLED_INTEGRATION_ENTITLEMENT_KEY ||
          entitlements?.includes(`${entitlement_key}`),
      )
      .map((integration: ConnectIntegrationResponse) => {
        const extraInfo = integrationsExtraInformation.find(
          ({ partner_id }) => partner_id === integration.partner_enum,
        );

        const integrationKey = `${integration.partner_enum}.${integration.name
          .toLowerCase()
          .replaceAll(' ', '_')
          .replaceAll("'", '')}`;

        return { ...integration, integrationKey, ...extraInfo };
      });

    runInAction(() => {
      this._integrations = new Integrations(integrationOptions);
    });
    return this._integrations;
  }

  async initialize_integrations() {
    if (this.gettingIntegrations) return;

    useIntegrationsStore.getState().setIsLoadingAuthAccounts(true);
    this._integrations = await this.loadIntegrations();
    runInAction(() => {
      this.gettingIntegrations = true;
    });
    const accounts = await Valet.getAuthAccounts(this.user_id, this.org_id);
    useIntegrationsStore.getState().setIsLoadingAuthAccounts(false);
    useIntegrationsStore.getState().setAuthAccounts(accounts);

    if (get(accounts, 'error_type') || get(accounts, 'message')) {
      runInAction(() => {
        this._status = APIState.FAIL;
      });
      if (get(accounts, 'message', '').includes('do not exist')) {
        return;
      }
      this.new_notification(IntegrationErrorTypes.GET_INTEGRATIONS_ERROR);
      return;
    }

    accounts
      .filter((account: { partner: string }) => account.partner !== 'cropwise')
      .forEach((account: AuthAccount) => {
        const integration = this._integrations?.findIntegrationByPartner(account.partner);

        if (integration) {
          integration.is_configured = account.auth_config;
          integration.user_id = account.user_id;
          integration.settings = { ...account.settings, account_id: account.account_id };
        }
      });

    runInAction(() => {
      this.gettingIntegrations = false;
      this._integrations.integrations = [...this._integrations.integrations];
      this._active_integration = this._active_integration
        ? this._integrations.integrations.find((item) => item.id === this._active_integration?.id)
        : undefined;
      this._status = APIState.SUCCESS;
    });
  }

  init(integrationKey: string, partner_id: string) {
    switch (integrationKey) {
      case IntegrationKeys.JohnDeereOperationsCenter:
        JDModel.redirect_to_oauth_portal().catch((error) => {
          console.error(error);
        });
        break;
      case IntegrationKeys.TrimbleTrimble:
        setStorage('trimble_connect_redirect', true);
        TrimbleModel.redirectToAuthentication().catch((error) => {
          console.error(error);
        });
        break;
      case IntegrationKeys.CnhiCnhi:
        try {
          this.CNH.init();
        } catch (error) {
          console.error(error);
        }
        break;
      case IntegrationKeys.Pessl:
      case IntegrationKeys.Farmobile:
      case IntegrationKeys.AgfinitiAgfiniti:
        redirectToOAuthPortal(partner_id)?.catch((e) => {
          console.error(e);
        });
        break;
      default:
        break;
    }
  }

  setBaseOrganizations(_orgs: any[]) {
    runInAction(() => {
      this._baseOrganizations = _orgs;
    });
  }
  setSelectedOrganizations(_orgs: any[]) {
    runInAction(() => {
      this._selectedOrganizations = _orgs;
    });
  }
  setDataRange(start?: string, end?: string) {
    if (start) {
      this._startDate = start;
    }
    if (end) {
      this._endDate = end;
    }
  }
  setPercentageRange(upTo?: boolean, between?: boolean) {
    if (upTo) {
      this._upTo = upTo;
    }
    if (between) {
      this._between = between;
    }
  }
  setAutoFFT(value: string) {
    setStorage('sync_fft', value);
    runInAction(() => {
      this.sync_fft = value;
    });
  }

  async setActiveIntegration(partner_id?: string) {
    let active_int: Integration | null = null;
    if (partner_id) {
      if (this._integrations.isEmpty()) await this.loadIntegrations();
      if (partner_id === PartnerId.Trimble2) partner_id = PartnerId.Trimble;
      active_int = this._integrations.findIntegrationByPartner(partner_id) ?? null;
    }

    runInAction(() => {
      this._active_integration = active_int;
    });
    if (!active_int) {
      this.set_subheader_default();
      return;
    }
    this.set_subheader(active_int.integration_name, partner_id as PartnerId);
  }

  async setActiveIntegrationByIntegrationKey(integrationKey?: string) {
    let active_int: Integration | null = null;

    const integrationList = isEmpty(this._integrations._integrations)
      ? await this.loadIntegrations()
      : this._integrations;

    if (integrationKey) {
      active_int = (await integrationList.find_by_integration_key(integrationKey)) ?? null;
    }

    runInAction(() => {
      this._active_integration = active_int;
    });
    if (!active_int) {
      this.set_subheader_default();
      return;
    }
    if (integrationKey) {
      this.set_subheader(
        active_int.integration_name,
        getPartnerIdFromIntegrationKey(integrationKey),
      );
    }
  }

  async findByIntegrationKey(integrationKey: string) {
    if (this._integrations.isEmpty()) {
      await this.loadIntegrations();
    }
    return this._integrations.find_by_integration_key(integrationKey);
  }

  async submitIntegration(partner_id: string, settings: any, override?: boolean) {
    runInAction(() => {
      this._setupIntegrationStatus = APIState.LOADING;
    });
    const innerSettings = {
      ...settings,
      auto_fft: override ? { is_active: override } : this._auto_fft,
    };

    const response = await Valet.persistIntegration(
      this.user_id,
      this.org_id,
      partner_id,
      innerSettings,
      undefined,
      this._active_integration?.id,
    );

    this.handle_successful_integration(innerSettings, partner_id as PartnerId);

    if (response.error_type) {
      this.new_notification(IntegrationErrorTypes.PATCH_INTEGRATION_ERROR);
    }

    this.initialize_integrations();

    runInAction(() => {
      this._setupIntegrationStatus = APIState.SUCCESS;
    });
    return response;
  }

  handle_successful_integration(history: any, partner_id: PartnerId, override?: boolean) {
    Valet.trigger_ingestion(this.user_id, this.org_id, partner_id)
      .then(() => {})
      .catch((error) => {
        console.error(error);
      });

    this.resetIntegration();

    if (this.deepAction) {
      this.new_notification(IntegrationErrorTypes.DEEP_ACTION_SUCCESS, {
        button: () => this.complete_deep_action('SUCCESS'),
      });
    }
  }

  complete_deep_action(result: DeepActionResult, error_code?: string) {
    this._rootStore.complete_deep_action(result, error_code);
  }

  resetIntegration() {
    setStorage('ingestion_status', null);
    runInAction(() => {
      this._selectedOrganizations = [];
      this._upTo = false;
      this._between = false;
      this._startDate = '';
      this._endDate = '';
      this._auto_fft = { is_active: false };
      this.sync_fft = '';
    });
  }

  async getOrganizations(partner_id: string) {
    let orgs: any = [];
    runInAction(() => {
      this._loadOrganizationsStatus = APIState.LOADING;
    });
    switch (partner_id) {
      case PartnerId.JohnDeere:
        orgs = await JDModel.get_orgs(this.user_id, this.org_id);
        break;
      case PartnerId.Trimble:
      case PartnerId.Trimble2: {
        const accessToken = getStorage('trimble_access_token');
        if (accessToken) {
          orgs = await TrimbleModel.getOrgs(accessToken);
        }
        break;
      }
      case PartnerId.CNH:
        orgs = await this.CNH.getOrganizations(this.user_id, this.org_id);
        break;
      case PartnerId.Proagrica:
        orgs = await getProagricaOrganizations(this._rootStore.activeUser);
        break;
    }
    runInAction(() => {
      this._baseOrganizations = orgs?.error_type ? [] : orgs;
      this._loadOrganizationsStatus = APIState.SUCCESS;
    });
    return orgs;
  }

  async getOrganizationsByIntegrationKey(integrationKey: IntegrationKeys) {
    const getOrgs: Record<string, any> = {
      [IntegrationKeys.JohnDeereOperationsCenter]: JDModel.get_orgs(this.user_id, this.org_id),
      [IntegrationKeys.CnhiCnhi]: this.CNH.getOrganizations(this.user_id, this.org_id),
      [IntegrationKeys.Proagrica]: getProagricaOrganizations(this._rootStore.activeUser),
    };

    if (!getOrgs[integrationKey]) return [];

    const orgs = await getOrgs[integrationKey];
    runInAction(() => {
      this._baseOrganizations = orgs?.error_type ? [] : orgs;
    });
    return orgs;
  }

  async getProagricaOrganizations() {
    try {
      let response = await getProagricaOrganizations(this._rootStore.activeUser);
      response = response.map((org: any) => {
        org.name = org.Name;
        org.id = org.ID;
        delete org.Name;
        delete org.ID;
        return org;
      });
      runInAction(() => {
        this._baseOrganizations = response;
      });
      return response;
    } catch (err: any) {
      console.error('Proagrica Org error:', err);
    }
  }

  async checkForIntegration(integrations: PartnerId | PartnerId[]) {
    let ints: PartnerId[];
    if (typeof integrations === 'string') {
      ints = [integrations];
    } else {
      ints = [...integrations];
    }

    let promises: any = [];
    ints.forEach((partner_id: string) => {
      const currentPromise = Valet.get_auth_token(this.user_id, this.org_id, partner_id);
      promises.push(currentPromise);
    });
    const partners: any = {};
    const allResponses = await Promise.all(promises);
    allResponses.forEach((promiseResults: any, index: number) => {
      partners[ints[index]] = !!promiseResults?.auth_config;
    });
    return partners;
  }

  async postProagricaUser() {
    try {
      return await postProagricaUser(this._rootStore.activeUser, this._rootStore.userEmail);
    } catch (err: any) {
      console.error('Post Proagrica user error:', err);
    }
  }

  async handle_partner_callback(partner_id: string) {
    let response: any = null;
    switch (partner_id) {
      case PartnerId.Proagrica:
        response = await this.postProagricaUser();
        break;
      case PartnerId.JohnDeere:
      case PartnerId.CNH:
        response = await Valet.send_oauth_code(
          this.user_id,
          this.org_id,
          partner_id as PartnerId,
          redirectURI(partner_id),
        );
        break;
      default:
        break;
    }

    if (response?.error_type) {
      this.new_notification(IntegrationErrorTypes.PARTNER_INTEGRATION_ERROR);
      return response;
    }
    if (response?.status === 201 || response?.status === 204) {
      setStorage('ingestion_status', response.status);
    }

    const orgsResponse = await this.getOrganizations(partner_id);
    if (orgsResponse?.error_type) {
      this.new_notification(IntegrationErrorTypes.ORGANIZATIONS_ERROR);
      return orgsResponse;
    }
    this.setBaseOrganizations(orgsResponse);
    return orgsResponse;
  }

  async handleTrimbleGetTokenAndRedirectToGrantOrgs(code: string) {
    const response = await TrimbleModel.getTokenAndRefreshToken(code, this.user_id, this.org_id);
    if (response.status >= 200 && response.status < 300) {
      TrimbleModel.redirectToGrantOrgs();
    }
  }

  setActiveUser(activeUser: string) {
    runInAction(() => {
      this._activeUser = activeUser;
    });
  }

  deselectOrganizations(orgs: string | string[]) {
    let newOrgs: string[];
    if (typeof orgs === 'string') {
      newOrgs = [orgs];
    } else {
      newOrgs = [...orgs];
    }
    let currentlySelected = [...this._selectedOrganizations];
    newOrgs.forEach((field_id: string) => {
      const index = currentlySelected.findIndex(
        (selectedField: string) => selectedField === field_id,
      );
      if (index === -1) {
        return;
      }
      currentlySelected.splice(index, 1);
    });
    runInAction(() => {
      this._selectedOrganizations = currentlySelected;
    });
  }

  toggleOrganizationSelected(organizationIds: string | string[]) {
    const idsToToggle = isArray(organizationIds) ? organizationIds : [organizationIds];
    const nonExistingToAdd = idsToToggle.filter((id) => !this._selectedOrganizations?.includes(id));
    const existingToRemove = this._selectedOrganizations?.filter((id) => idsToToggle.includes(id));
    const others = this._selectedOrganizations?.filter(
      (id) => !existingToRemove.includes(id) && !nonExistingToAdd.includes(id),
    );

    const result = new Set(
      this._selectedOrganizations
        ?.filter((id) => !existingToRemove.includes(id))
        .concat(nonExistingToAdd)
        .concat(others),
    );

    runInAction(() => {
      this._selectedOrganizations = [...result];
    });
  }

  selectOrganizations(orgs: string | string[]) {
    const newOrgs = typeof orgs === 'string' ? [orgs] : [...orgs];

    const concatenated = [...this._selectedOrganizations, ...newOrgs];
    const final: string[] = [];
    concatenated.forEach((c) => {
      if (!final.includes(c)) {
        final.push(c);
      }
    });
    runInAction(() => {
      this._selectedOrganizations = final;
    });
  }

  new_notification(error_type: IntegrationErrorTypes, _note?: any, partner_id?: string) {
    const note: DatabusNotification = {
      placement: 'topRight',
      description: 'From Integrations',
      message: 'Message success!',
    };

    switch (error_type) {
      case partner_id === PartnerId.CNH && IntegrationErrorTypes.PARTNER_INTEGRATION_ERROR:
        note.message = 'Integration Error';
        note.description = '404 Error: Most likely a mismatch of Staging Env - Connect -> CNH';
        note.type = 'ERROR';
        break;
      case IntegrationErrorTypes.PARTNER_INTEGRATION_ERROR:
        note.message = 'Integration Error';
        note.description = `Failed to communicate with${' ' + this.activeIntegration?.name}`;
        note.type = 'ERROR';
        break;
      case IntegrationErrorTypes.ORGANIZATIONS_ERROR:
        note.message = 'Integration Error';
        note.description = `Failed to requestion organizations from${
          ' ' + this.activeIntegration?.name
        }`;
        note.type = 'ERROR';
        break;
      case IntegrationErrorTypes.PATCH_INTEGRATION_ERROR:
        note.message = 'Integration Error';
        note.description = `Failed to save integration preferences for${
          ' ' + this.activeIntegration?.name
        }`;
        note.type = 'ERROR';
        break;
      case IntegrationErrorTypes.PATCH_INTEGRATION_SUCCESS:
        note.message = 'Integration Successful';
        note.description =
          'Your data will begin to import. You will receive a notification once the import is complete.';
        const auto_fft = getStorage('auto_fft');
        if (auto_fft?.is_active) {
          note.description = (
            <>
              {note.description}
              <br />
              <br />
              You can manage fields and properties in Cropwise Base.
            </>
          );
        }
        break;
      case IntegrationErrorTypes.DEEP_ACTION_SUCCESS:
        note.message = 'Integration Successful';
        note.description = 'Return to previous application';
        note.button = _note?.button;
        break;
      case IntegrationErrorTypes.GET_INTEGRATIONS_ERROR:
        note.message = 'Integrations Error';
        note.description = 'Failed to retrieve integrations';
        note.type = 'ERROR';
        break;
      case IntegrationErrorTypes.AUTHENTICATION_ERROR:
        note.message = 'Integration Error';
        note.description = 'Failed to validate your credentials';
        note.type = 'ERROR';
        break;
      case IntegrationErrorTypes.INTEGRATION_ERROR:
        note.message = 'Integration Error';
        note.description = '';
        note.type = 'ERROR';
        break;
      case IntegrationErrorTypes.INTEGRATION_SUCCESS:
        note.message = 'Integration Successful';
        note.description = '';
        break;
      default:
        break;
    }
    this._rootStore.set_notification(note);
  }

  getAllIntegrations() {
    return this._integrations.get_all_integrations();
  }

  setSelectedIntegrationTab(_selectedTab?: string) {
    const selectedTab = _selectedTab ?? IntegrationConnectionType.machinery;
    runInAction(() => {
      this.selectedIntegrationTab = capitalize(selectedTab);
    });
  }

  set active_integration_token(activeToken: Token | null) {
    this._active_integration_token = activeToken;
  }
  get active_integration_token() {
    return this._active_integration_token;
  }
  get active_access_token() {
    return this._active_integration_token?.access_token;
  }
  get baseOrganizations() {
    return [...this._baseOrganizations].slice();
  }
  get selectedOrganizations() {
    return this._selectedOrganizations;
  }
  get activeIntegration() {
    return this._active_integration;
  }
  get settings() {
    return this._settings;
  }
  get activeUser() {
    return this._activeUser;
  }
  get user_id() {
    return this._rootStore.userId;
  }
  get org_id() {
    return this._rootStore.orgId;
  }
  get workspace_id() {
    return this._rootStore.auth.defaultWorkspaceId;
  }
  get activeOrganization() {
    return this._rootStore.auth.activeOrganization;
  }
  get status() {
    return this._status;
  }
  get setupIntegrationStatus() {
    return this._setupIntegrationStatus;
  }

  set status(_status: APIState) {
    this._status = _status;
  }

  get integrations() {
    return this._integrations.get_all_integrations();
  }

  get deepAction() {
    return this._rootStore.deepAction;
  }

  set token(token: string) {
    this._token = token;
  }
  get token() {
    return this._token;
  }

  set crumbs(_crumbs: any[]) {
    this._crumbs = _crumbs;
  }

  get crumbs() {
    return this._crumbs;
  }

  set selectedIntegrationTab(_selectedTab: string) {
    this._selectedIntegrationTab = _selectedTab;
  }

  get selectedIntegrationTab() {
    return this._selectedIntegrationTab;
  }

  get searchString() {
    return this._searchString;
  }

  set searchString(searchString: string) {
    runInAction(() => {
      this._searchString = searchString;
    });
  }

  set organizationCountryCode(countryCode: string) {
    runInAction(() => {
      this._organizationCountryCode = countryCode;
    });
  }

  get organizationCountryCode() {
    return this._organizationCountryCode;
  }
}
